import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChildren,
  QueryList,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { MainMenuItem, MenuItem } from '../../interfaces/menu.interface';
import { MenuButtonComponent } from "../menu-button/menu-button.component";
import { RoutingService } from '../services/routing.service';
import { UserService } from '../../services/user.service';
import { FocusTrapDirective } from '../directives/focus-trap.directive';

@Component({
  selector: 'ppl-menu-drop-button',
  standalone: true,
  imports: [CommonModule, MenuButtonComponent],
  templateUrl: './menu-drop-button.component.html',
  styleUrl: './menu-drop-button.component.css'
})
export class MenuDropButtonComponent implements OnInit, AfterViewInit {
  @Input() label = 'Dropdown';
  @Input() icon?: string;
  @Input() iconPosition: 'left' | 'right' = 'right';
  @Input() type = 'button';
  @Input() disabled = false;
  @Input() customClass = '';
  @Input() menuItems?: MainMenuItem;

  showDropdown = false;
  activeIndex = 0;

  @ViewChildren('mi', { read: ElementRef }) itemEls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('triggerBtn', { read: ElementRef }) triggerBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('menuPanel',  { read: ElementRef }) menuPanel!: ElementRef<HTMLElement>;

  private _awaitingFirstFocus = false;

  constructor(
    private eRef: ElementRef,
    private routerService: RoutingService,
    private userService: UserService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.itemEls.changes.subscribe(() => {
      if (this.activeIndex > this.itemEls.length - 1) {
        this.activeIndex = Math.max(0, this.itemEls.length - 1);
      }
      if (this._awaitingFirstFocus && this.itemEls.length > 0) {
        this._awaitingFirstFocus = false;
        this.focusActive();
      }
    });
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) {
      this.activeIndex = 0;
      this.focusFirstItemWhenReady();
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.showDropdown) this.showDropdown = true;
      this.activeIndex = event.key === 'ArrowDown' ? 0 : Math.max(0, this.itemEls.length - 1);
      this.focusFirstItemWhenReady();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.showDropdown = true;
      this.activeIndex = 0;
      this.focusFirstItemWhenReady();
    } else if (event.key === 'Escape' && this.showDropdown) {
      event.preventDefault();
      this.showDropdown = false;
      this.triggerBtn?.nativeElement.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!this.eRef.nativeElement.contains(target)) {
      this.showDropdown = false;
    }
  }


  onItemKeydown(e: KeyboardEvent, item: MenuItem, idx: number): void {
    e.stopPropagation(); 

    const max = this.itemEls.length - 1;
    const go = (i: number) => { this.activeIndex = i; this.focusActive(); };

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = (idx >= max) ? 0 : idx + 1; 
        go(next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = (idx <= 0) ? max : idx - 1; 
        go(prev);
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        break;
      }
      case 'Home':
        e.preventDefault(); go(0); break;
      case 'End':
        e.preventDefault(); go(max); break;

      case 'Tab': {
        e.preventDefault();
        if (e.shiftKey) {
          this.closeAndFocusPrev();
        } else {
          this.closeAndFocusNext();
        }
        break;
      }

      case 'Escape':
        e.preventDefault();
        this.showDropdown = false;
        this.triggerBtn?.nativeElement.focus();
        break;

      case 'Enter':
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        e.stopPropagation();
        this.OnMenuClick(item);
        break;
    }
  }

  private closeAndFocusNext(): void {
    this.showDropdown = false;
    this.focusNextAfter(this.menuPanel?.nativeElement || this.triggerBtn.nativeElement);
  }

  private closeAndFocusPrev(): void {
    this.showDropdown = false;
    this.focusPrevBefore(this.menuPanel?.nativeElement || this.triggerBtn.nativeElement);
  }

  OnMenuClick(item: MenuItem): void {
    this.showDropdown = false;
    const route = `/home/${item.landing_component}`;
    this.userService.setFirstSegment(item.landing_component ?? '');
    setTimeout(() => this.routerService.navigate(route), 0);
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  getFlatIndex(category: string, innerIndex: number): number {
    if (!this.menuItems) return -1;
    let idx = 0;
    for (const cat of this.objectKeys(this.menuItems)) {
      const list = this.menuItems[cat] ?? [];
      for (let i = 0; i < list.length; i++) {
        const it = list[i];
        if (it.display === 'y') {
          if (cat === category && i === innerIndex) return idx;
          idx++;
        }
      }
    }
    return -1;
  }

  private focusFirstItemWhenReady(): void {
    if (this.itemEls && this.itemEls.length > 0) {
      this.focusActive();
      return;
    }
    this._awaitingFirstFocus = true;
    setTimeout(() => {
      if (this.itemEls && this.itemEls.length > 0) {
        this._awaitingFirstFocus = false;
        this.focusActive();
      }
    }, 0);
  }

  private focusActive(): void {
    const el = this.itemEls.get(this.activeIndex)?.nativeElement;
    el?.focus();
  }

  private focusNextAfter(rootEl: HTMLElement): void {
    const all = this.getFocusable();
    const current = document.activeElement as HTMLElement | null;
    const startIdx = current ? all.indexOf(current) : all.indexOf(rootEl);
    const next = all[startIdx + 1] || all[0];
    next?.focus();
  }

  private focusPrevBefore(rootEl: HTMLElement): void {
    const all = this.getFocusable();
    const current = document.activeElement as HTMLElement | null;
    const startIdx = current ? all.indexOf(current) : all.indexOf(rootEl);
    const prev = all[startIdx - 1] || all[all.length - 1];
    prev?.focus();
  }

  private getFocusable(): HTMLElement[] {
    const sel = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    const nodes = Array.from(document.querySelectorAll<HTMLElement>(sel));
    return nodes.filter(el => this.isVisible(el));
  }

  private isVisible(el: HTMLElement): boolean {
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }
}
